import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { SwPush } from '@angular/service-worker';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { PushNotificationsApiService } from '@blaze-runtime/pwa-data-access';
import { PushSubscriptionRequest } from '@blaze-shared/api-contracts-domain';
import {
  LocalStorageService,
  NotificationService,
  StorageKey,
} from '@blaze-shared/platform-data-access';
import { base64ToUint8Array } from '@blaze-shared/shared-util';

import {
  EnableNotificationsDialogComponent,
  EnableNotificationsDialogInput,
} from './enable-notifications-dialog/enable-notifications-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  private readonly dialog = inject(MatDialog);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly pushNotificationsApiService = inject(PushNotificationsApiService);
  private readonly swPush = inject(SwPush);

  protected readonly messages = toSignal(this.swPush.messages);

  constructor() {
    this.swPush.messages.subscribe((message) => {
      console.log('Push notification received', message);
    });
  }

  /**
   * Triggers flow for enabling push notifications (showing dialog, enabling subscription)
   * @param projectId - ID of the project for which the push notification should be requested.
   */
  enableNotifications(projectId: string): void {
    const dissmissalTimestamp = this.localStorageService.getForProject(
      projectId,
      StorageKey.NotificationPermissionDismissal,
    );

    // Check whether user has dismissed the dialog in the last 7 days. If yes, do not show the dialog.
    const sevenDaysInMillis = 7 * 24 * 60 * 60 * 1000;
    if (dissmissalTimestamp && Date.now() - Number(dissmissalTimestamp) < sevenDaysInMillis) {
      const days = (Date.now() - Number(dissmissalTimestamp)) / (1000 * 60 * 60 * 24);
      console.log(`User has dismissed the dialog ${days} days ago`);
      return;
    }

    this.showRequestNotificationAccessDialog(projectId)
      .pipe(
        switchMap((isPermissionGranted) => {
          if (!isPermissionGranted) {
            console.warn('Notification permission not granted');
            return of(null);
          }
          return this.subscribeToNotifications(projectId);
        }),
      )
      .subscribe();
  }

  private showRequestNotificationAccessDialog(projectId: string): Observable<boolean> {
    // The Notification global is absent in some contexts (e.g. iOS Safari outside an
    // installed PWA), where merely referencing it throws a ReferenceError. Bail out
    // early so we skip the dialog and dismissal bookkeeping on unsupported browsers.
    if (typeof Notification === 'undefined') {
      console.warn('Push notifications are not supported in this browser.');
      return of(false);
    }

    console.log(`Current notification permission`, Notification.permission);
    if (Notification.permission === 'granted') {
      return of(true);
    }
    if (Notification.permission === 'denied') {
      return of(false);
    }
    return this.dialog
      .open(EnableNotificationsDialogComponent, {
        data: { projectId } satisfies EnableNotificationsDialogInput,
        disableClose: false,
        hasBackdrop: true,
        height: '539px;',
        maxHeight: '539px',
        maxWidth: '420px',
        panelClass: ['bd', 'bd-dialog'],
      })
      .afterClosed();
  }

  /**
   * Subscribes to notifications.
   * @returns - Observable with a boolean indicating whether subscription was successful.
   */
  private subscribeToNotifications(projectId: string): Observable<boolean> {
    if (!this.swPush.isEnabled) {
      console.warn('Push notifications are not enabled in this browser.');
      return of(false);
    }

    console.log(`Subscribing to push notifications...`);
    return this.pushNotificationsApiService.getKeys().pipe(
      map((keysResp) => {
        return keysResp?.publicKey;
      }),
      switchMap(async (publicKey): Promise<any> => {
        if (!publicKey) {
          console.error(`Unable to subscribe to notifications. Public key is not available.`);
          return null;
        }

        try {
          // Use native pushManager.subscribe instead of swPush.requestSubscription:
          // SwPush gates on Angular's NgswCommChannel.isEnabled flag, which can be
          // false even when the SW is registered and active (e.g. zoneless mode,
          // registerImmediately race) — leading to a spurious NG05601 error.
          //
          // pushManager.subscribe throws InvalidStateError if an existing
          // subscription was created with a different VAPID key, so unsubscribe first.
          console.log(`Waiting for service worker to be ready...`);
          const registration = await navigator.serviceWorker.ready;
          console.log(`Fetching existing subscription`);
          const existingSubscription = await registration.pushManager.getSubscription();

          if (existingSubscription) {
            console.log('Reusing existing push subscription');
            return existingSubscription;
          }
          const sub = await registration.pushManager.subscribe({
            applicationServerKey: base64ToUint8Array(publicKey),
            userVisibleOnly: true,
          });
          return sub;
        } catch (err) {
          console.error('Could not subscribe to notifications', err);
          return null;
        }
      }),
      switchMap((sub: PushSubscription | null) => {
        if (!sub) {
          console.error('Unable to subscribe to notification. Push subscription was not created');
          return of(false);
        }
        const subJson = sub.toJSON();
        const request: PushSubscriptionRequest = {
          endpoint: subJson.endpoint ?? '',
          keys: {
            auth: subJson.keys?.auth ?? null,
            p256dh: subJson.keys?.p256dh ?? null,
          },
          projectId,
        };
        console.log('Sending subscription information...', sub);
        return this.pushNotificationsApiService.sendSubscription(request);
      }),
      map(() => true),
      catchError((err) => {
        this.notificationService.showError(err);
        return of(false);
      }),
    );
  }
}
